import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Menu } from 'lucide-react';
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { AppDispatch } from '../../redux/store';
import { logout } from '../../redux/auth/authSlice';
import Sidebar from '../../components/sidebar/Sidebar';
import TaapTabs from '../../components/taap/TaapTabs';
import {
  ResponsiveAppShell,
  TaskyPageHeader,
  TaskySelect,
  TaskyStatCard,
  useConfirm,
} from '../../components/tasky';
import { TaskyHeaderActions } from '../../components/tasky/layout/TaskyHeaderActions';
import googleAdsService from '../../services/api/googleAdsService';
import {
  GoogleAdsConnection,
  GoogleAdsChangeInput,
  GoogleAdsChangeRequest,
  GoogleAdsDailyPoint,
  GoogleAdsDashboard,
  GoogleAdsDatePreset,
  GoogleAdsReportLevel,
} from '../../types/googleAds';

type TrendMetric = keyof GoogleAdsDailyPoint['metrics'];

const TREND_METRICS: { value: TrendMetric; label: string; kind: 'money' | 'percent' | 'plain' }[] = [
  { value: 'cost', label: '費用', kind: 'money' },
  { value: 'impressions', label: '表示回数', kind: 'plain' },
  { value: 'clicks', label: 'クリック', kind: 'plain' },
  { value: 'conversions', label: 'コンバージョン', kind: 'plain' },
  { value: 'ctr', label: 'CTR', kind: 'percent' },
  { value: 'average_cpc', label: '平均CPC', kind: 'money' },
];

type GoogleAdsOperationType = GoogleAdsChangeInput['operation_type'];
type WithoutIdempotencyKey<T> = T extends { idempotency_key: string } ? Omit<T, 'idempotency_key'> : never;
type GoogleAdsChangeDraft = WithoutIdempotencyKey<GoogleAdsChangeInput>;

// ============================================================
// Tailwind class constants (旧 styled-components)
// ============================================================

const PAGE = 'min-h-[calc(100dvh_-_56px)] bg-slate-50 text-gray-900';

// TaapTabs を出す 5 画面で本体幅を揃える（タブ切替で幅と位置が動かないようにする）。
const CONTAINER = 'w-full max-w-[1400px] mx-auto p-6 max-[640px]:p-[14px]';

const CARD = 'bg-white border border-gray-200 rounded-xl p-[18px] mb-[18px]';

const FILTER_ROW = 'flex items-end gap-3 flex-wrap';

const FIELD = 'grid gap-[5px] text-slate-600 text-[12px] min-w-[150px]';

const INPUT = 'border border-slate-300 rounded-lg px-[10px] py-[9px] text-[14px] text-gray-900 bg-white';

const SELECT = 'h-9 text-sm';

const TEXTAREA = 'border border-slate-300 rounded-lg px-[10px] py-[9px] text-[14px] min-h-[90px] resize-y';

const BUTTON_BASE = 'border rounded-lg px-[14px] py-[9px] text-[13px] font-bold cursor-pointer disabled:opacity-[0.55] disabled:cursor-not-allowed';
const BUTTON_DEFAULT = `${BUTTON_BASE} border-slate-300 bg-white text-slate-700`;
const BUTTON_PRIMARY = `${BUTTON_BASE} border-[#1a73e8] bg-[#1a73e8] text-white`;
const BUTTON_DANGER = `${BUTTON_BASE} border-red-600 bg-red-600 text-white`;

const KPI_GRID = 'grid grid-cols-[repeat(5,minmax(140px,1fr))] gap-3 mb-[18px] max-[1050px]:grid-cols-3 max-[640px]:grid-cols-2';

const SECTION_TITLE = 'text-[17px] m-0 mb-3';

const MUTED = 'text-slate-500 text-[12px] leading-[1.6]';

const ALERT_BASE = 'border rounded-[9px] px-[13px] py-[11px] text-[13px] mb-[14px]';
const ALERT_INFO = `${ALERT_BASE} border-blue-200 bg-blue-50 text-blue-800`;
const ALERT_ERROR = `${ALERT_BASE} border-red-200 bg-red-50 text-red-700`;

const TABLE_WRAP = 'overflow-auto border border-gray-200 rounded-[10px]';

// th/td の共通指定は子孫セレクタの arbitrary variant で再現
const TABLE = 'w-full min-w-[1050px] border-collapse '
  + '[&_th]:px-3 [&_th]:py-2.5 [&_th]:border-b [&_th]:border-b-slate-100 [&_th]:text-right [&_th]:text-[12px] [&_th]:whitespace-nowrap '
  + '[&_td]:px-3 [&_td]:py-2.5 [&_td]:border-b [&_td]:border-b-slate-100 [&_td]:text-right [&_td]:text-[12px] [&_td]:whitespace-nowrap '
  + '[&_th]:bg-slate-50 [&_th]:text-slate-500 [&_th]:font-bold '
  + '[&_th:first-child]:text-left [&_td:first-child]:text-left';

const PROPOSAL_GRID = 'grid grid-cols-2 gap-3 max-[800px]:grid-cols-1';

const PROPOSAL_BASE = 'border border-gray-200 border-l-4 rounded-[10px] p-[15px]';
// #dc2626=red-600 / #f59e0b=amber-500 / #16a34a=green-600 / #64748b=slate-500
const PROPOSAL_SEVERITY: Record<string, string> = {
  high: 'border-l-red-600',
  medium: 'border-l-amber-500',
  low: 'border-l-green-600',
};
const PROPOSAL_SEVERITY_FALLBACK = 'border-l-slate-500';

const PROPOSAL_TITLE = 'font-extrabold text-[14px] mb-1.5';

const METRIC_CHIPS = 'flex gap-1.5 flex-wrap my-2.5';

const CHIP = 'rounded-full bg-slate-100 text-slate-700 px-2 py-1 text-[11px]';

const FORM_GRID = 'grid grid-cols-2 gap-3 max-[700px]:grid-cols-1';

const PREVIEW = 'border-2 border-blue-300 bg-[#f8fbff] rounded-[10px] p-4 mt-4';

const DEFINITION = 'grid grid-cols-[180px_1fr] gap-x-3 gap-y-[7px] text-[13px] '
  + '[&_dt]:text-slate-500 [&_dd]:m-0 [&_dd]:font-semibold [&_dd]:[word-break:break-word] '
  + 'max-[640px]:grid-cols-[1fr]';

const STATUS = 'inline-block px-2 py-[3px] rounded-full bg-slate-100 text-[11px] font-bold';

const AUDIT_DETAIL = 'text-left '
  + '[&_summary]:text-blue-700 [&_summary]:cursor-pointer [&_summary]:font-bold '
  + '[&_pre]:max-w-[520px] [&_pre]:max-h-[260px] [&_pre]:overflow-auto [&_pre]:mt-2 [&_pre]:mx-0 [&_pre]:mb-0 '
  + '[&_pre]:p-2.5 [&_pre]:rounded-lg [&_pre]:bg-slate-900 [&_pre]:text-slate-200 [&_pre]:text-[11px] '
  + '[&_pre]:whitespace-pre-wrap [&_pre]:[word-break:break-word]';

const LEVELS: { value: GoogleAdsReportLevel; label: string }[] = [
  { value: 'account', label: 'アカウント' },
  { value: 'campaign', label: 'キャンペーン' },
  { value: 'ad_group', label: '広告グループ' },
  { value: 'ad', label: '広告' },
  { value: 'search_term', label: '検索語句' },
];

function errorMessage(error: unknown, fallback: string): string {
  const typed = error as { response?: { data?: { message?: string } } };
  return typed.response?.data?.message || fallback;
}

function metric(value: number | null | undefined, options?: { money?: string | null; percent?: boolean; suffix?: string }): string {
  if (value === null || value === undefined) return '対象外／未設定';
  const formatted = new Intl.NumberFormat('ja-JP', {
    maximumFractionDigits: options?.percent ? 2 : 2,
  }).format(value);
  if (options?.money) return `${options.money} ${formatted}`;
  return `${formatted}${options?.percent ? '%' : options?.suffix || ''}`;
}

function conversionCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return '対象外／未設定';
  // Fractional attribution can yield CV < 0.01. Preserve the number's
  // significant digits without exposing binary sum noise such as 0.1 + 0.2.
  return new Intl.NumberFormat('ja-JP', { maximumSignificantDigits: 15 }).format(value);
}

function formatMicros(micros: number | null | undefined, currency?: string | null): string {
  if (micros === null || micros === undefined) return '取得できませんでした';
  return metric(micros / 1_000_000, { money: currency });
}

/**
 * 前期間比の文言。母数が0の期間はバックエンドが null を返す（+100% と出すと
 * 実際に倍増したように読めるため）ので、ここでも数字を作らない。
 *
 * 増加=良いとは限らない指標（費用・CPA）があるので、色で良し悪しを断じず
 * 向きだけを示す。
 */
function describeDelta(percent: number | null): string {
  if (percent === null || !Number.isFinite(percent)) return '前期間比 —';
  const arrow = percent > 0 ? '▲' : percent < 0 ? '▼' : '±';
  const formatted = new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 1 })
    .format(Math.abs(percent));
  return `前期間比 ${arrow}${formatted}%`;
}

function today(daysAgo = 0): string {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().slice(0, 10);
}

const GoogleAdsDashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [connection, setConnection] = useState<GoogleAdsConnection | null>(null);
  const [connectionAttempt, setConnectionAttempt] = useState(0);
  const refreshAfterConnection = useRef(false);
  const [loadedDashboard, setLoadedDashboard] = useState<{ scope: string; value: GoogleAdsDashboard } | null>(null);
  const [trendMetric, setTrendMetric] = useState<TrendMetric>('cost');
  const [audit, setAudit] = useState<GoogleAdsChangeRequest[]>([]);
  const accountParam = searchParams.get('account');
  const accountId = (connection?.accounts.find((account) => String(account.id) === accountParam)
    ?? connection?.accounts[0])?.id;
  const [preset, setPreset] = useState<GoogleAdsDatePreset>('last_30d');
  const [startDate, setStartDate] = useState(today(29));
  const [endDate, setEndDate] = useState(today());
  const [level, setLevel] = useState<GoogleAdsReportLevel>('campaign');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [savedPreview, setPreview] = useState<GoogleAdsChangeRequest | null>(null);
  const preview = savedPreview?.account.id === accountId ? savedPreview : null;
  const [operationType, setOperationType] = useState<GoogleAdsOperationType>('campaign_create');
  const [campaignName, setCampaignName] = useState(`Tasky staging ${new Date().toISOString().slice(0, 16)}`);
  const [dailyBudget, setDailyBudget] = useState('1000');
  const [adGroupName, setAdGroupName] = useState('Tasky staging ad group');
  const [cpcBid, setCpcBid] = useState('100');
  const [keywords, setKeywords] = useState('tasky\n広告 改善');
  const [headlines, setHeadlines] = useState('広告運用をもっと簡単に\nデータから改善案を提案\nTaskyで広告成果を確認');
  const [descriptions, setDescriptions] = useState('広告実績を一元化し、根拠のある改善案を確認できます。\n承認してから反映するため、安全に広告運用を進められます。');
  const [finalUrl, setFinalUrl] = useState('https://stage.tasky-ai.com/');
  // campaign_budget_update
  const [budgetCampaignResourceName, setBudgetCampaignResourceName] = useState('');
  const [newDailyBudgetMicros, setNewDailyBudgetMicros] = useState('1000000000');
  // campaign_bid_update
  const [bidAdGroupResourceName, setBidAdGroupResourceName] = useState('');
  const [newCpcBidMicros, setNewCpcBidMicros] = useState('100000000');
  // ad_replace
  const [adReplaceResourceName, setAdReplaceResourceName] = useState('');
  const [adReplaceHeadlines, setAdReplaceHeadlines] = useState('広告運用をもっと簡単に\nデータから改善案を提案\nTaskyで広告成果を確認');
  const [adReplaceDescriptions, setAdReplaceDescriptions] = useState('広告実績を一元化し、根拠のある改善案を確認できます。\n承認してから反映するため、安全に広告運用を進められます。');
  const [adReplaceFinalUrl, setAdReplaceFinalUrl] = useState('https://stage.tasky-ai.com/');
  const idempotencyRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const confirm = useConfirm();

  const scope = JSON.stringify([accountId, preset, startDate, endDate, level]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const currentAccount = useRef(accountId);
  currentAccount.current = accountId;
  const loadId = useRef(0);
  const dashboard = loadedDashboard?.scope === scope ? loadedDashboard.value : null;

  const selectAccount = useCallback((id?: number) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (id) next.set('account', String(id));
      else next.delete('account');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    let active = true;
    googleAdsService.getConnection().then((next) => {
      if (active) setConnection(next);
    }).catch((err) => {
      if (active) {
        setError(errorMessage(err, 'Google広告の連携情報を取得できませんでした。'));
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, [connectionAttempt]);

  useEffect(() => {
    if (connection && accountParam !== (accountId ? String(accountId) : null)) {
      selectAccount(accountId);
    }
  }, [connection, accountParam, accountId, selectAccount]);

  useEffect(() => {
    setPreview(null);
    setNotice(null);
    setAudit([]);
    idempotencyRef.current = null;
  }, [accountId]);

  const load = useCallback(async (force = false) => {
    if (!connection) {
      if (force) {
        setLoading(true);
        setError(null);
        refreshAfterConnection.current = true;
        setConnectionAttempt((attempt) => attempt + 1);
      }
      return;
    }
    const requestId = ++loadId.current;
    const isCurrent = () => currentScope.current === scope && requestId === loadId.current;
    setLoading(true);
    setError(null);
    setLoadedDashboard(null);
    setAudit([]);
    const refresh = force || refreshAfterConnection.current;
    refreshAfterConnection.current = false;
    try {
      const data = await googleAdsService.getDashboard({
        accountId,
        preset,
        startDate: preset === 'custom' ? startDate : undefined,
        endDate: preset === 'custom' ? endDate : undefined,
        level,
        refresh,
        includeDaily: true,
        includeComparison: true,
      });
      if (!isCurrent()) return;
      setNeedsReauth(false);
      setLoadedDashboard({ scope, value: data });
      const nextAudit = data.selected_account?.id
        ? await googleAdsService.getAudit(data.selected_account.id)
        : [];
      if (isCurrent()) setAudit(nextAudit);
    } catch (err) {
      if (!isCurrent()) return;
      const apiError = (err as {
        response?: { data?: { error?: { category?: string; needs_reauth?: boolean } } };
      }).response?.data?.error;
      setNeedsReauth(apiError?.needs_reauth === true || apiError?.category === 'authentication');
      setError(errorMessage(err, 'Google広告データを取得できませんでした。'));
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [connection, accountId, preset, startDate, endDate, level, scope]);

  useEffect(() => {
    load();
    return () => { loadId.current += 1; };
  }, [load]);

  const selectedTrendMetric = useMemo(
    () => TREND_METRICS.find((item) => item.value === trendMetric) ?? TREND_METRICS[0],
    [trendMetric],
  );

  const trendData = useMemo(
    () => (dashboard?.daily ?? []).map((point) => ({
      date: point.date.slice(5), // MM-DD。年は期間ラベル側に出ている
      value: point.metrics[trendMetric],
    })),
    [dashboard, trendMetric],
  );

  const formatTrendValue = useCallback(
    (value: number | null): string => {
      if (value === null) return '対象外／未設定';
      if (selectedTrendMetric.value === 'conversions') return conversionCount(value);
      if (selectedTrendMetric.kind === 'money') {
        return metric(value, { money: dashboard?.summary?.currency_code });
      }
      if (selectedTrendMetric.kind === 'percent') {
        return metric(value, { percent: true });
      }
      return metric(value);
    },
    [selectedTrendMetric, dashboard],
  );

  const kpis = useMemo((): [string, string, number | null][] => {
    const summary = dashboard?.summary;
    const currency = summary?.currency_code;
    const delta = dashboard?.comparison?.delta;
    return [
      ['費用', metric(summary?.cost, { money: currency }), delta?.cost_percent ?? null],
      ['表示回数', metric(summary?.impressions), delta?.impressions_percent ?? null],
      ['クリック', metric(summary?.clicks), delta?.clicks_percent ?? null],
      ['CTR', metric(summary?.ctr, { percent: true }), delta?.ctr_percent ?? null],
      ['平均CPC', metric(summary?.average_cpc, { money: currency }), delta?.average_cpc_percent ?? null],
      ['コンバージョン', conversionCount(summary?.conversions), delta?.conversions_percent ?? null],
      ['CVR', metric(summary?.conversion_rate, { percent: true }), null],
      ['CPA', metric(summary?.cost_per_conversion, { money: currency }), delta?.cost_per_conversion_percent ?? null],
      ['コンバージョン値', metric(summary?.conversion_value), null],
      ['ROAS', metric(summary?.roas, { suffix: '倍' }), null],
    ];
  }, [dashboard]);

  const buildDraft = (accountId: number): GoogleAdsChangeDraft => {
    switch (operationType) {
      case 'campaign_budget_update':
        return {
          account_id: accountId,
          operation_type: 'campaign_budget_update',
          campaign_resource_name: budgetCampaignResourceName.trim(),
          new_daily_budget_micros: Number(newDailyBudgetMicros),
        };
      case 'campaign_bid_update':
        return {
          account_id: accountId,
          operation_type: 'campaign_bid_update',
          ad_group_resource_name: bidAdGroupResourceName.trim(),
          new_cpc_bid_micros: Number(newCpcBidMicros),
        };
      case 'ad_replace':
        return {
          account_id: accountId,
          operation_type: 'ad_replace',
          ad_group_ad_resource_name: adReplaceResourceName.trim(),
          headlines: adReplaceHeadlines.split('\n').map((text) => text.trim()).filter(Boolean),
          descriptions: adReplaceDescriptions.split('\n').map((text) => text.trim()).filter(Boolean),
          final_url: adReplaceFinalUrl.trim(),
        };
      case 'campaign_create':
      default:
        return {
          account_id: accountId,
          operation_type: 'campaign_create',
          campaign_name: campaignName.trim(),
          daily_budget: Number(dailyBudget),
          ad_group_name: adGroupName.trim(),
          cpc_bid: Number(cpcBid),
          keywords: keywords.split('\n').map((text) => text.trim()).filter(Boolean)
            .map((text) => ({ text, match_type: 'PHRASE' as const })),
          headlines: headlines.split('\n').map((text) => text.trim()).filter(Boolean),
          descriptions: descriptions.split('\n').map((text) => text.trim()).filter(Boolean),
          final_url: finalUrl.trim(),
        };
    }
  };

  const createPreview = async () => {
    if (!dashboard?.selected_account || dashboard.selected_account.id !== accountId || loading) return;
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const draft = buildDraft(dashboard.selected_account.id);
      const fingerprint = JSON.stringify(draft);
      if (idempotencyRef.current?.fingerprint !== fingerprint) {
        idempotencyRef.current = {
          fingerprint,
          key: typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `tasky-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        };
      }
      const change = await googleAdsService.createChange({
        ...draft,
        idempotency_key: idempotencyRef.current.key,
      });
      if (currentAccount.current !== accountId) return;
      setPreview(change);
      setNotice('変更案を保存しました。対象・予算・入札・広告文を確認してください。まだGoogle Adsへは反映されていません。');
    } catch (err) {
      if (currentAccount.current === accountId) setError(errorMessage(err, '変更案を作成できませんでした。'));
    } finally {
      setSubmitting(false);
    }
  };

  const validatePreview = async () => {
    if (!preview) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await googleAdsService.validateChange(preview.id);
      if (currentAccount.current !== accountId) return;
      setNotice(`Google Ads APIのvalidate_onlyに成功しました。Request ID: ${result.request_id || 'なし'}`);
    } catch (err) {
      if (currentAccount.current === accountId) setError(errorMessage(err, 'Google Ads APIの事前検証に失敗しました。'));
    } finally {
      setSubmitting(false);
    }
  };

  const executePreview = async () => {
    if (!preview) return;
    const actionLabel = preview.operation_type === 'campaign_create'
      ? 'PAUSED状態でキャンペーン・広告を作成します'
      : preview.operation_type === 'ad_replace'
        ? '新しい広告をPAUSEDで作成し、旧広告をPAUSEDにします'
        : '変更を反映します（ENABLEDへは変更されません）';
    const confirmed = await confirm({
      title: `Customer ID •••• ${preview.account.customer_id_suffix} に、${actionLabel}。実行しますか？`,
      tone: 'destructive',
      confirmText: '実行する',
    });
    if (!confirmed) return;

    setSubmitting(true);
    setError(null);
    try {
      const executed = await googleAdsService.executeChange(preview.id);
      if (currentAccount.current !== accountId) return;
      setPreview(executed);
      setNotice(`Google Adsへ反映しました。ステータス: ${executed.status}`);
      const nextAudit = await googleAdsService.getAudit(accountId);
      if (currentAccount.current === accountId) setAudit(nextAudit);
    } catch (err) {
      if (currentAccount.current !== accountId) return;
      setError(errorMessage(err, 'Google Adsへの反映に失敗しました。監査ログを確認してください。'));
      const nextAudit = await googleAdsService.getAudit(accountId);
      if (currentAccount.current === accountId) setAudit(nextAudit);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const sidebar = (
    <Sidebar
      onLogout={handleLogout}
      onToggleCollapse={setSidebarCollapsed}
      collapsed={sidebarCollapsed}
    />
  );

  return (
    <ResponsiveAppShell
      sidebar={sidebar}
      mobileSidebar={<Sidebar onLogout={handleLogout} />}
      // スマホのトップバー用。ページ名だと「…」で切れるので、下の媒体タブと同じ名前にする
      topbarTitle="Google広告"
    >
      <div className={PAGE}>
        <TaskyPageHeader
          className="hidden lg:block"
          title={
            <span className="inline-flex items-center gap-3">
              {sidebarCollapsed && <Menu size={20} />}
              Google Ads ダッシュボード
            </span>
          }
          actions={<TaskyHeaderActions />}
        />
        <main className={CONTAINER}>
          <div className="mb-5" data-tutorial="ad-dashboard-tabs"><TaapTabs active="google" /></div>
          {error && (
            <div className={ALERT_ERROR}>
              <p>{error}</p>
              {needsReauth && (
                <button className={BUTTON_PRIMARY} onClick={() => navigate('/settings/media-connect')}>
                  Google広告を再連携する
                </button>
              )}
            </div>
          )}
          {notice && <div className={ALERT_INFO}>{notice}</div>}

          <section className={CARD} data-tutorial="ad-dashboard-filter">
            <div className={FILTER_ROW}>
              <label className={FIELD}>
                広告アカウント
                <TaskySelect className={SELECT} value={accountId || ''} disabled={submitting} onChange={(e) => selectAccount(Number(e.target.value))}>
                  {(connection?.accounts || []).map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.account_name} (•••• {account.customer_id_suffix})
                    </option>
                  ))}
                </TaskySelect>
              </label>
              <label className={FIELD}>
                期間
                <TaskySelect className={SELECT} value={preset} onChange={(e) => setPreset(e.target.value as GoogleAdsDatePreset)}>
                  <option value="last_7d">過去7日</option>
                  <option value="last_30d">過去30日</option>
                  <option value="custom">任意期間</option>
                </TaskySelect>
              </label>
              {preset === 'custom' && (
                <>
                  <label className={FIELD}>開始日<input className={INPUT} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></label>
                  <label className={FIELD}>終了日<input className={INPUT} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></label>
                </>
              )}
              <label className={FIELD}>
                粒度
                <TaskySelect className={SELECT} value={level} onChange={(e) => setLevel(e.target.value as GoogleAdsReportLevel)}>
                  {LEVELS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </TaskySelect>
              </label>
              <button className={BUTTON_DEFAULT} onClick={() => load(true)} disabled={loading}>再同期</button>
            </div>
            <div className={MUTED} style={{ marginTop: 10 }}>
              {dashboard?.source || 'Google Ads API v22 GAQL'}
              {dashboard?.summary?.time_zone ? ` / タイムゾーン ${dashboard.summary.time_zone}` : ''}
              {dashboard?.sync?.synced_at ? ` / 最終同期 ${new Date(dashboard.sync.synced_at).toLocaleString('ja-JP')}` : ''}
            </div>
          </section>

          {!loading && dashboard?.connected === false && (
            <section className={CARD}>
              <h2 className={SECTION_TITLE}>Google広告が未連携です</h2>
              <button className={BUTTON_PRIMARY} onClick={() => navigate('/settings/media-connect')}>媒体連携設定へ</button>
            </section>
          )}

          {loading && <section className={CARD}><div className={MUTED}>Google Ads APIから実績を取得しています...</div></section>}

          {dashboard?.summary && (
            <>
              <div className={KPI_GRID} data-tutorial="ad-dashboard-kpi">
                {kpis.map(([label, value, deltaPercent]) => (
                  <TaskyStatCard
                    key={label}
                    label={label}
                    value={value}
                    delta={describeDelta(deltaPercent)}
                  />
                ))}
              </div>

              <section className={CARD}>
                <div className={FILTER_ROW}>
                  <h2 className={SECTION_TITLE} style={{ marginBottom: 0, marginRight: 'auto' }}>日別推移</h2>
                  <label className={FIELD}>
                    指標
                    <TaskySelect
                      className={SELECT}
                      value={trendMetric}
                      onChange={(event) => setTrendMetric(event.target.value as TrendMetric)}
                    >
                      {TREND_METRICS.map((item) => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                      ))}
                    </TaskySelect>
                  </label>
                </div>
                {trendData.length === 0 ? (
                  <div className={MUTED} style={{ marginTop: 10 }}>
                    指定期間に日別の実績がありません。同期エラーではなく、該当する配信実績が0件です。
                  </div>
                ) : (
                  <div style={{ marginTop: 12 }}>
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={trendData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                        <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                        <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" width={70} />
                        <Tooltip
                          formatter={(value) => formatTrendValue(
                            typeof value === 'number' ? value : null,
                          )}
                          labelFormatter={(label) => `${label}`}
                        />
                        <Line
                          type="monotone"
                          dataKey="value"
                          name={selectedTrendMetric.label}
                          stroke="#1a73e8"
                          strokeWidth={2}
                          dot={false}
                          connectNulls={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {dashboard.comparison && (
                  <div className={MUTED} style={{ marginTop: 10 }}>
                    {`前期間 ${dashboard.comparison.period.start_date} 〜 ${dashboard.comparison.period.end_date} と比較しています。`}
                    {'（母数が0の期間は変化率を表示しません）'}
                  </div>
                )}
              </section>

              <section className={CARD} data-tutorial="ad-dashboard-table">
                <h2 className={SECTION_TITLE}>明細（{LEVELS.find((item) => item.value === level)?.label}）</h2>
                {dashboard.rows.length === 0 ? (
                  <div className={MUTED}>指定期間の配信実績はありません。</div>
                ) : (
                  <div className={TABLE_WRAP}>
                    <table className={TABLE}>
                      <thead>
                        <tr>
                          <th>対象</th><th>状態</th><th>費用</th><th>表示</th><th>クリック</th>
                          <th>CTR</th><th>平均CPC</th><th>CV</th><th>CVR</th><th>CPA</th><th>CV値</th><th>ROAS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.rows.slice(0, 200).map((row) => (
                          <tr key={`${row.entity.type}-${row.entity.id}`}>
                            <td>{row.entity.name || `ID ${row.entity.id}`}</td>
                            <td>{row.entity.status || '—'}</td>
                            <td>{metric(row.metrics.cost, { money: dashboard.summary?.currency_code })}</td>
                            <td>{metric(row.metrics.impressions)}</td>
                            <td>{metric(row.metrics.clicks)}</td>
                            <td>{metric(row.metrics.ctr, { percent: true })}</td>
                            <td>{metric(row.metrics.average_cpc, { money: dashboard.summary?.currency_code })}</td>
                            <td>{conversionCount(row.metrics.conversions)}</td>
                            <td>{metric(row.metrics.conversion_rate, { percent: true })}</td>
                            <td>{metric(row.metrics.cost_per_conversion, { money: dashboard.summary?.currency_code })}</td>
                            <td>{metric(row.metrics.conversion_value)}</td>
                            <td>{metric(row.metrics.roas, { suffix: '倍' })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section className={CARD}>
                <h2 className={SECTION_TITLE}>根拠数値付きの改善示唆</h2>
                <div className={PROPOSAL_GRID}>
                  {dashboard.proposals.map((proposal) => (
                    <article
                      key={proposal.id}
                      className={`${PROPOSAL_BASE} ${PROPOSAL_SEVERITY[proposal.severity] || PROPOSAL_SEVERITY_FALLBACK}`}
                    >
                      <div className={PROPOSAL_TITLE}>{proposal.category}: {proposal.target.name}</div>
                      <div className={MUTED}>{proposal.period.start_date} 〜 {proposal.period.end_date}</div>
                      <div className={METRIC_CHIPS}>
                        {proposal.metrics.map((item) => (
                          <span className={CHIP} key={item.label}>
                            {item.label}: {item.value ?? '対象外'}{item.unit ? ` ${item.unit}` : ''}
                          </span>
                        ))}
                      </div>
                      <div style={{ fontSize: 13, lineHeight: 1.7 }}>{proposal.recommendation}</div>
                      <div className={MUTED} style={{ marginTop: 7 }}>期待する変化: {proposal.expected_change}</div>
                      <span className={CHIP} style={{ marginTop: 10 }}>{proposal.actionability === 'api_available' ? 'API反映可能' : proposal.actionability === 'data_insufficient' ? 'データ不足' : '提案のみ'}</span>
                    </article>
                  ))}
                </div>
              </section>

              <section className={CARD} data-testid="google-ads-change-form">
                <h2 className={SECTION_TITLE}>Google Ads 変更案</h2>
                <div className={ALERT_INFO}>
                  変更案を作成しただけではGoogle Adsへ反映されません。確認画面で対象・変更前・変更後・予算・入札・広告文を確認し、承認後にのみ実行します。ENABLEDへの変更はできません。
                </div>
                {dashboard.write_enabled !== true && (
                  <div className={ALERT_ERROR}>
                    この環境ではGoogle Adsへの書き込みが無効です。変更案の確認はできますが、事前検証と反映は実行できません。
                  </div>
                )}
                <label className={FIELD} style={{ marginBottom: 12 }}>
                  変更の種類
                  <TaskySelect
                    className={SELECT}
                    value={operationType}
                    onChange={(e) => { setOperationType(e.target.value as GoogleAdsOperationType); setPreview(null); }}
                  >
                    <option value="campaign_create">新規キャンペーン作成（PAUSED）</option>
                    <option value="campaign_budget_update">既存キャンペーンの予算変更</option>
                    <option value="campaign_bid_update">既存広告グループの入札変更</option>
                    <option value="ad_replace">広告文の差し替え（新規PAUSED作成 + 旧広告停止）</option>
                  </TaskySelect>
                </label>

                {operationType === 'campaign_create' && (
                  <div className={FORM_GRID}>
                    <label className={FIELD}>キャンペーン名<input className={INPUT} value={campaignName} onChange={(e) => setCampaignName(e.target.value)} /></label>
                    <label className={FIELD}>日予算（{dashboard.summary.currency_code || '通貨'}）<input className={INPUT} type="number" value={dailyBudget} onChange={(e) => setDailyBudget(e.target.value)} /></label>
                    <label className={FIELD}>広告グループ名<input className={INPUT} value={adGroupName} onChange={(e) => setAdGroupName(e.target.value)} /></label>
                    <label className={FIELD}>上限CPC（{dashboard.summary.currency_code || '通貨'}）<input className={INPUT} type="number" value={cpcBid} onChange={(e) => setCpcBid(e.target.value)} /></label>
                    <label className={FIELD}>キーワード（1行1件・フレーズ一致）<textarea className={TEXTAREA} value={keywords} onChange={(e) => setKeywords(e.target.value)} /></label>
                    <label className={FIELD}>見出し（1行1件・3〜15件）<textarea className={TEXTAREA} value={headlines} onChange={(e) => setHeadlines(e.target.value)} /></label>
                    <label className={FIELD}>説明文（1行1件・2〜4件）<textarea className={TEXTAREA} value={descriptions} onChange={(e) => setDescriptions(e.target.value)} /></label>
                    <label className={FIELD}>最終ページURL<input className={INPUT} value={finalUrl} onChange={(e) => setFinalUrl(e.target.value)} /></label>
                  </div>
                )}

                {operationType === 'campaign_budget_update' && (
                  <div className={FORM_GRID}>
                    <label className={FIELD}>
                      対象キャンペーン（customers/.../campaigns/...）
                      <input
                        className={INPUT}
                        value={budgetCampaignResourceName}
                        onChange={(e) => setBudgetCampaignResourceName(e.target.value)}
                        placeholder="customers/.../campaigns/..."
                      />
                    </label>
                    <label className={FIELD}>
                      新しい日予算（micros）
                      <input className={INPUT} type="number" value={newDailyBudgetMicros} onChange={(e) => setNewDailyBudgetMicros(e.target.value)} />
                    </label>
                  </div>
                )}

                {operationType === 'campaign_bid_update' && (
                  <div className={FORM_GRID}>
                    <label className={FIELD}>
                      対象広告グループ（customers/.../adGroups/...）
                      <input className={INPUT} value={bidAdGroupResourceName} onChange={(e) => setBidAdGroupResourceName(e.target.value)} />
                    </label>
                    <label className={FIELD}>
                      新しい上限CPC（micros）
                      <input className={INPUT} type="number" value={newCpcBidMicros} onChange={(e) => setNewCpcBidMicros(e.target.value)} />
                    </label>
                  </div>
                )}

                {operationType === 'ad_replace' && (
                  <div className={FORM_GRID}>
                    <label className={FIELD}>
                      差し替え対象の広告（customers/.../adGroupAds/...）
                      <input className={INPUT} value={adReplaceResourceName} onChange={(e) => setAdReplaceResourceName(e.target.value)} />
                    </label>
                    <label className={FIELD}>見出し（1行1件・3〜15件）<textarea className={TEXTAREA} value={adReplaceHeadlines} onChange={(e) => setAdReplaceHeadlines(e.target.value)} /></label>
                    <label className={FIELD}>説明文（1行1件・2〜4件）<textarea className={TEXTAREA} value={adReplaceDescriptions} onChange={(e) => setAdReplaceDescriptions(e.target.value)} /></label>
                    <label className={FIELD}>最終ページURL<input className={INPUT} value={adReplaceFinalUrl} onChange={(e) => setAdReplaceFinalUrl(e.target.value)} /></label>
                  </div>
                )}
                <button className={BUTTON_PRIMARY} onClick={createPreview} disabled={submitting || loading || dashboard.selected_account?.id !== accountId} style={{ marginTop: 14 }}>
                  変更案を確認
                </button>

                {preview && (
                  <div className={PREVIEW} data-testid="google-ads-change-preview">
                    <h2 className={SECTION_TITLE}>最終確認（まだ未実行）</h2>
                    <dl className={DEFINITION}>
                      <dt>対象アカウント</dt><dd>{preview.account.account_name} / Customer ID •••• {preview.account.customer_id_suffix}</dd>
                      <dt>アカウント種別</dt><dd>{preview.account.is_test_account ? 'Google Adsテストアカウント' : '許可リスト対象'}</dd>
                      {preview.operation_type === 'campaign_create' && (
                        <>
                          <dt>変更前</dt><dd>新規作成（既存リソースの上書きなし）</dd>
                          <dt>キャンペーン</dt><dd>{preview.after.campaign_name} / PAUSED</dd>
                          <dt>日予算</dt><dd>{preview.after.daily_budget} {dashboard.summary.currency_code}</dd>
                          <dt>入札方式</dt><dd>{preview.after.bidding_strategy} / CPC {preview.after.cpc_bid}</dd>
                          <dt>広告グループ</dt><dd>{preview.after.ad_group_name} / PAUSED</dd>
                          <dt>キーワード</dt><dd>{preview.after.keywords.map((item: { text: string }) => item.text).join('、')}</dd>
                          <dt>広告見出し</dt><dd>{preview.after.headlines.join(' / ')}</dd>
                          <dt>説明文</dt><dd>{preview.after.descriptions.join(' / ')}</dd>
                          <dt>広告状態</dt><dd>PAUSED</dd>
                        </>
                      )}
                      {preview.operation_type === 'campaign_budget_update' && (
                        <>
                          <dt>対象キャンペーン</dt>
                          <dd>{(preview.before?.campaign_name as string) || preview.after.campaign_resource_name}</dd>
                          <dt>変更前 日予算</dt>
                          <dd>{formatMicros(preview.before?.daily_budget_micros as number | undefined, dashboard.summary.currency_code)}</dd>
                          <dt>変更後 日予算</dt>
                          <dd>{formatMicros(preview.after.new_daily_budget_micros, dashboard.summary.currency_code)}</dd>
                        </>
                      )}
                      {preview.operation_type === 'campaign_bid_update' && (
                        <>
                          <dt>対象広告グループ</dt>
                          <dd>
                            {(preview.before?.ad_group_name as string) || preview.after.ad_group_resource_name}
                            {preview.before?.campaign_name ? `（${preview.before.campaign_name as string}）` : ''}
                          </dd>
                          <dt>変更前 上限CPC</dt>
                          <dd>{formatMicros(preview.before?.cpc_bid_micros as number | undefined, dashboard.summary.currency_code)}</dd>
                          <dt>変更後 上限CPC</dt>
                          <dd>{formatMicros(preview.after.new_cpc_bid_micros, dashboard.summary.currency_code)}</dd>
                        </>
                      )}
                      {preview.operation_type === 'ad_replace' && (
                        <>
                          <dt>変更前 見出し</dt>
                          <dd>{(preview.before?.headlines as string[] | undefined)?.join(' / ') || '取得できませんでした'}</dd>
                          <dt>変更前 説明文</dt>
                          <dd>{(preview.before?.descriptions as string[] | undefined)?.join(' / ') || '取得できませんでした'}</dd>
                          <dt>変更後 見出し</dt><dd>{preview.after.headlines.join(' / ')}</dd>
                          <dt>変更後 説明文</dt><dd>{preview.after.descriptions.join(' / ')}</dd>
                          <dt>広告状態</dt><dd>新規PAUSED作成 / 旧広告PAUSED化</dd>
                        </>
                      )}
                      <dt>現在の処理状態</dt><dd><span className={STATUS}>{preview.status}</span></dd>
                    </dl>
                    {preview.resource_names.length > 0 && (
                      <div className={MUTED} style={{ marginTop: 10 }}>作成リソース: {preview.resource_names.join(', ')}</div>
                    )}
                    {preview.google_request_id && (
                      <div className={MUTED}>Google Request ID: {preview.google_request_id}</div>
                    )}
                    {preview.error_summary && <div className={ALERT_ERROR}>{preview.error_summary}</div>}
                    <div className={FILTER_ROW} style={{ marginTop: 14 }}>
                      <button
                        className={BUTTON_DEFAULT}
                        onClick={validatePreview}
                        disabled={submitting || preview.status !== 'pending_confirmation' || dashboard.write_enabled !== true}
                      >
                        Google APIで事前検証
                      </button>
                      <button
                        className={BUTTON_DANGER}
                        onClick={executePreview}
                        disabled={submitting || preview.status !== 'pending_confirmation' || dashboard.write_enabled !== true}
                      >
                        内容を承認してPAUSEDで反映
                      </button>
                    </div>
                  </div>
                )}
              </section>

              <section className={CARD}>
                <h2 className={SECTION_TITLE}>Google Ads書き込み監査ログ</h2>
                {audit.length === 0 ? <div className={MUTED}>書き込み操作はまだありません。</div> : (
                  <div className={TABLE_WRAP}>
                    <table className={TABLE}>
                      <thead><tr><th>日時・操作</th><th>実行者</th><th>対象</th><th>状態</th><th>Request ID</th><th>Resource</th><th>変更・エラー詳細</th></tr></thead>
                      <tbody>
                        {audit.map((item) => (
                          <tr key={item.id}>
                            <td>{new Date(item.created_at).toLocaleString('ja-JP')} / {item.operation_type}</td>
                            <td>{item.approved_by?.name || item.requested_by.name || `User ${item.requested_by.id}`}</td>
                            <td>•••• {item.account.customer_id_suffix}</td>
                            <td><span className={STATUS}>{item.status}</span>{item.error_summary ? ` / ${item.error_summary}` : ''}</td>
                            <td>{item.google_request_id || '—'}</td>
                            <td>{item.resource_names.join(', ') || '—'}</td>
                            <td>
                              <details className={AUDIT_DETAIL}>
                                <summary>変更前後を確認</summary>
                                <pre>{JSON.stringify({
                                  before: item.before,
                                  after: item.after,
                                  validation_errors: item.validation_errors,
                                }, null, 2)}</pre>
                              </details>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}
        </main>
      </div>
    </ResponsiveAppShell>
  );
};

export default GoogleAdsDashboardPage;
